import log from "loglevel";
import moment from "moment";
import {
  createClient,
  parseNumUri,
  DefaultCallbackHandler,
  DoHResolver,
  PositiveInteger,
} from "num-client";
import ContactsModuleHelper from "num-js-modules/src/contacts";

import { mapGetters, mapMutations, mapState } from "vuex";

import { API_ENDPOINT, STAGE } from "@/app.config";

import {
  CLEAR_PROMISE,
  SET_PROMISE,
  UPDATE_NUM_RESULT,
} from "@/store/types/mutations";

const getLogLevel = () => {
  switch (STAGE) {
    case "local":
      return "warn";
    case "test":
      return "debug";
    case "staging":
      return "warn";
    default:
      return "silent";
  }
};
const logLevel = getLogLevel();
log.setLevel(logLevel);

export default {
  data() {
    return {
      client: null,
      content: null,
      dohServers: {
        test: {
          name: "NUM Test DoH",
          params: ["name", "type", "dnssec"],
          text: "Test",
          url: "https://api.test.numops.com/api/v1/doh/dns-query",
        },
        staging: {
          name: "NUM Staging DoH",
          params: ["name", "type", "dnssec"],
          text: "Staging",
          url: "https://api.staging.numops.com/api/v1/doh/dns-query",
        },
        prod: {
          name: "NUM Live DoH",
          params: ["name", "type", "dnssec"],
          text: "Live",
          url: "https://api.apps.num.uk/v1/doh/dns-query",
        },
      },
      errorCode: null,
      isPopulated: null,
      json: null,
      moduleId: null,
      numUri: null,
      numUriString: null,
      queryLocation: null,
      querying: null,
      usingCachedResult: false,
    };
  },
  computed: {
    ...mapGetters(["getCachedNumResult"]),
    ...mapState(["_promises", "settings"]),
    formattedNumId() {
      return this.numUriString && parseNumUri(this.numUriString);
    },
  },
  watch: {
    settings() {
      // make sure that the client is wiped if the settings change to stop
      // clients using old settings being reused
      this.client = null;
    },
  },
  methods: {
    ...mapMutations([CLEAR_PROMISE, SET_PROMISE, UPDATE_NUM_RESULT]),
    async getNumResult(searchTerm, useCachedResult = true) {
      if (!searchTerm) return false;
      this.numUri = parseNumUri(searchTerm);
      this.numUri = this.numUri.withPort(new PositiveInteger(this.moduleId));
      this.numUriString = this.numUri.toString();
      this.$emit("num-uri-changed", this.numUriString);

      const cachedNumResult =
        useCachedResult && this.getCachedNumResult(this.numUriString);
      const cachedNumResultNotExpired =
        cachedNumResult && moment().diff(cachedNumResult.storedOn, "days") < 7;

      console.log("getNumResult()", this.numUriString);

      if (cachedNumResultNotExpired) {
        this.usingCachedResult = true;
        const { populated, result } = cachedNumResult;
        this.json = result;
        this.isPopulated = populated;
      } else {
        await this.submitNumQuery();
      }
    },
    resetValues() {
      this.content = null;
      this.lookup = null;
      this.json = null;
      this.usingCachedResult = false;
    },
    setModuleVersions(ctx) {
      if (this.numUri.port.n === 1) ctx.setTargetExpandedSchemaVersion("2");
    },
    setupClient() {
      const resolvers = [
        new DoHResolver(`NUM ${STAGE} DoH`, `${API_ENDPOINT}/doh/dns-query`),
      ];
      this.client = createClient(resolvers);

      const { dnsEnvironment, modulesEnvironment } = this.settings;
      this.client.setDnsEnv(dnsEnvironment);
      this.client.setModuleEnv(modulesEnvironment);
    },
    async submitNumQuery() {
      if (!this.numUri) return;
      if (!this.client) this.setupClient();
      this.querying = true;
      this.resetValues();

      const existingPromise = this._promises[this.numUriString];
      if (existingPromise) return existingPromise;

      // this is the amount of time we keep the promise for after returning, to
      // stop endpoints being re-queried if it was queried milliseconds before.
      // this stops the case of a resource being requested, returning very fast,
      // before it is re-requested and then querying again.
      const timeBeforeAllowingReRequest = 1000;

      const clearPromise = () => {
        setTimeout(
          () => this.CLEAR_PROMISE(this.numUriString),
          timeBeforeAllowingReRequest,
        );
      };

      const handleAnyResponse = () => {
        clearPromise();
      };

      const ctx = this.client.createContext(this.numUri);
      this.setModuleVersions(ctx);
      const { countryCode, languageCode } = this.settings;
      ctx.setUserVariable("_C", countryCode);
      ctx.setUserVariable("_L", languageCode);

      // this is setup outside the context of the `Handler` class, as `this` is
      // bound to that class context within there, so would need to do `const
      // that = this` and use `that` inside there which seems messier
      const handlerMethods = {
        setErrorCode: errorCode => {
          this.json = null;
          this.isPopulated = null;
          this.errorCode = errorCode;
        },
        setLocation: location => {
          this.queryLocation = location;
        },
        setResult: result => {
          this.json = JSON.parse(result);
          this.isPopulated = !!this.json?.["@p"];
          this.errorCode = null;
          const moduleId = this.numUri.port.n;

          switch (moduleId) {
            case 1:
              this.json = ContactsModuleHelper.transform(
                this.json,
                ctx.userVariables,
                null,
              ).numObject;
              break;
          }

          this.updateNumResult(this.numUriString);
        },
      };

      class Handler extends DefaultCallbackHandler {
        setErrorCode = e => handlerMethods.setErrorCode(e);
        setLocation = l => handlerMethods.setLocation(l);
        setResult = r => handlerMethods.setResult(r);
      }

      const handler = new Handler();
      const promise = await this.client
        .retrieveNumRecord(ctx, handler)
        .then(result => {
          // if result is `null`, setResult isn't called, so handle it here
          if (result === null) this.json = null;

          this.content = ctx.result;
          this.$emit("searching", false);
          this.querying = false;
          handleAnyResponse();
        })
        .catch(() => {
          handleAnyResponse();
        });

      this.SET_PROMISE({
        key: this.numUriString,
        promise,
      });
    },
    updateNumResult(numUriString) {
      const timestampNow = moment().format();
      this.UPDATE_NUM_RESULT({
        numUriString: numUriString,
        persisted: this.persistRecord,
        populated: this.isPopulated,
        result: this.json,
        storedOn: timestampNow,
      });
    },
  },
};
